import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Wrench, Lock, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { updateMoldStatus } from '@/lib/api/molds';
import { apiGet } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import type { WOType, WOImpact } from '@/types/maintenance';

interface CreateWorkOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedMoldId?: string;
  preselectedType?: WOType;
  prefilledDescription?: string;
  /** When provided, called after WO creation instead of navigating to the new WO */
  onCreated?: (wo: { id: string; wo_id: string }) => void;
}

const typeLabels: Record<WOType, string> = {
  preventive: 'Preventive',
  corrective: 'Corrective',
  corrective_in_machine: 'Corrective In-Machine',
  improvement: 'Improvement',
};

const impactConfig: { value: WOImpact; label: string; className: string }[] = [
  { value: 'line_down', label: 'Line Down', className: 'border-[hsl(var(--priority-critical))] text-[hsl(var(--priority-critical))] bg-[hsl(var(--priority-critical)/0.1)] hover:bg-[hsl(var(--priority-critical)/0.15)]' },
  { value: 'safety',    label: 'Safety',    className: 'border-[hsl(var(--priority-high))] text-[hsl(var(--priority-high))] bg-[hsl(var(--priority-high)/0.1)] hover:bg-[hsl(var(--priority-high)/0.15)]' },
  { value: 'quality',   label: 'Quality',   className: 'border-[hsl(var(--priority-medium))] text-[hsl(var(--priority-medium))] bg-[hsl(var(--priority-medium)/0.12)] hover:bg-[hsl(var(--priority-medium)/0.18)]' },
  { value: 'performance', label: 'Performance', className: 'border-[hsl(var(--priority-low))] text-[hsl(var(--priority-low))] bg-[hsl(var(--priority-low)/0.1)] hover:bg-[hsl(var(--priority-low)/0.15)]' },
  { value: 'pm',        label: 'PM',        className: 'border-muted-foreground/40 text-foreground bg-secondary hover:bg-secondary/80' },
];

interface DBMold { id: string; mold_id: string; name: string; }
interface DBUser { id: string; name: string; role: string; }
interface DBMachine { id: string; machine_id: string; name: string; }

export function CreateWorkOrderDialog({
  open, onOpenChange, preselectedMoldId, preselectedType = 'preventive', prefilledDescription, onCreated,
}: CreateWorkOrderDialogProps) {
  const navigate = useNavigate();
  const { createWorkOrder, workOrders } = useWorkOrders();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ── Fetch dropdown data from DB ──────────────────────────────────────────
  const { data: molds = [] } = useQuery<DBMold[]>({
    queryKey: ['molds_dropdown'],
    queryFn: () => apiGet<DBMold[]>('/api/molds').then(rows =>
      rows.map(r => ({ id: r.id, mold_id: r.mold_id, name: r.name }))
    ),
    staleTime: 5 * 60_000,
  });

  const { data: technicians = [] } = useQuery<DBUser[]>({
    queryKey: ['technicians_dropdown'],
    queryFn: () => apiGet<DBUser[]>('/api/profiles').then(rows =>
      rows.filter(r => r.role === 'technician' || r.role === 'manager')
    ),
    staleTime: 5 * 60_000,
  });

  const { data: machines = [] } = useQuery<DBMachine[]>({
    queryKey: ['machines_dropdown'],
    queryFn: () => apiGet<DBMachine[]>('/api/machines'),
    staleTime: 5 * 60_000,
  });

  // ── Form state ───────────────────────────────────────────────────────────
  const [type, setType] = useState<WOType>(preselectedType);
  const [impact, setImpact] = useState<WOImpact>(preselectedType === 'preventive' ? 'pm' : 'performance');
  const [moldId, setMoldId] = useState(preselectedMoldId || '');
  const [machineId, setMachineId] = useState<string | undefined>();
  const [assignedTo, setAssignedTo] = useState<string | undefined>();
  const [failureText, setFailureText] = useState('');
  const [notes, setNotes] = useState('');
  const [moldLocked, setMoldLocked] = useState(!!preselectedMoldId);
  const [pullFromProduction, setPullFromProduction] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setType(preselectedType);
      setImpact(preselectedType === 'preventive' ? 'pm' : 'performance');
      setMoldId(preselectedMoldId || '');
      setMachineId(undefined);
      setAssignedTo(undefined);
      setFailureText(preselectedType === 'preventive' ? 'Preventive maintenance' : '');
      setNotes(prefilledDescription || '');
      setMoldLocked(!!preselectedMoldId);
      setPullFromProduction(false);
      setErrors({});
    }
  }, [open, preselectedMoldId, preselectedType, prefilledDescription]);

  useEffect(() => {
    setFailureText(type === 'preventive' ? 'Preventive maintenance' : '');
    setImpact(type === 'preventive' ? 'pm' : (impact === 'pm' ? 'performance' : impact));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const isCorrectiveOrImprovement = type === 'corrective' || type === 'improvement' || type === 'corrective_in_machine';

  const existingOpenWOs = moldId
    ? workOrders.filter(wo => wo.mold_id === moldId && wo.status !== 'closed')
    : [];

  const preselectedMold = preselectedMoldId ? molds.find(m => m.id === preselectedMoldId) : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const errs: Record<string, string> = {};

    if (!moldId) errs.mold_id = 'Mold is required';
    if (isCorrectiveOrImprovement && (!failureText.trim() || failureText.trim().length < 3)) {
      errs.failure = 'Failure is required (min 3 characters)';
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setIsSubmitting(false);
      return;
    }

    const description = isCorrectiveOrImprovement
      ? (notes.trim() ? `${failureText.trim()} — ${notes.trim()}` : failureText.trim())
      : (failureText.trim() || 'Preventive Maintenance');

    try {
      const newWO = await createWorkOrder({
        type,
        impact,
        mold_id: moldId,
        machine_id: machineId,
        assigned_to: assignedTo,
        problem_description: description,
        failure_text: failureText.trim() || undefined,
        parts_needed: false,
      });

      onOpenChange(false);
      if (pullFromProduction && moldId) {
        await updateMoldStatus(moldId, 'in_maintenance');
        queryClient.invalidateQueries({ queryKey: ['molds'] });
      }
      if (onCreated) {
        onCreated(newWO);
      } else {
        navigate(`/maintenance/work-orders/${newWO.id}`);
      }
    } catch {
      // Error toast is handled in the store
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Create Work Order
          </DialogTitle>
          <DialogDescription>Create a new work order for mold maintenance</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type */}
          <div className="space-y-2">
            <Label>Type *</Label>
            <Select value={type} onValueChange={(v) => setType(v as WOType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(typeLabels) as WOType[]).map(t => (
                  <SelectItem key={t} value={t}>{typeLabels[t]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Impact */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label>Impact *</Label>
              {type === 'preventive' && (
                <span className="text-xs text-muted-foreground">(auto-set for PM)</span>
              )}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {impactConfig.map(({ value, label, className }) => (
                <button
                  key={value}
                  type="button"
                  disabled={type === 'preventive'}
                  onClick={() => setImpact(value)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm font-medium border transition-all',
                    impact === value
                      ? className
                      : 'border-border text-muted-foreground bg-background hover:bg-muted',
                    type === 'preventive' && 'opacity-60 cursor-not-allowed',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Mold */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Mold *</Label>
              {preselectedMoldId && moldLocked && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setMoldLocked(false)} className="h-auto py-1 px-2 text-xs">
                  <X className="h-3 w-3 mr-1" />Change mold
                </Button>
              )}
            </div>
            {moldLocked && preselectedMold ? (
              <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/50">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{preselectedMold.mold_id}</span>
                <span className="text-muted-foreground">— {preselectedMold.name}</span>
              </div>
            ) : (
              <Select value={moldId} onValueChange={setMoldId}>
                <SelectTrigger className={errors.mold_id ? 'border-destructive' : ''}>
                  <SelectValue placeholder="Select a mold" />
                </SelectTrigger>
                <SelectContent>
                  {molds.map(mold => (
                    <SelectItem key={mold.id} value={mold.id}>
                      {mold.mold_id} — {mold.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {errors.mold_id && <p className="text-sm text-destructive">{errors.mold_id}</p>}
          </div>

          {/* Pull from production */}
          {isCorrectiveOrImprovement && moldId && (
            <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border bg-destructive/5 border-destructive/20">
              <Checkbox
                checked={pullFromProduction}
                onCheckedChange={(v) => setPullFromProduction(!!v)}
                className="mt-0.5"
              />
              <div>
                <p className="text-sm font-medium leading-tight">Pull mold from production immediately</p>
                <p className="text-xs text-muted-foreground mt-0.5">Sets mold status to "In Maintenance"</p>
              </div>
            </label>
          )}

          {/* Duplicate warning */}
          {existingOpenWOs.length > 0 && (
            <Alert className="bg-[hsl(var(--status-amber-bg))] border-[hsl(var(--status-amber))]/30">
              <AlertCircle className="h-4 w-4 text-[hsl(var(--status-amber))]" />
              <AlertDescription className="text-sm">
                Open work order{existingOpenWOs.length > 1 ? 's' : ''} exist for this mold:{' '}
                {existingOpenWOs.map((wo, i) => (
                  <span key={wo.id}>
                    {i > 0 && ', '}
                    <Link to={`/maintenance/work-orders/${wo.id}`} className="font-medium text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                      {wo.wo_id}
                    </Link>
                  </span>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {/* Failure / Request */}
          <div className="space-y-2">
            <Label>{isCorrectiveOrImprovement ? 'Failure *' : 'Failure / Request'}</Label>
            <Input
              value={failureText}
              onChange={(e) => setFailureText(e.target.value)}
              placeholder={isCorrectiveOrImprovement ? 'Describe the failure...' : 'Preventive maintenance'}
              className={errors.failure ? 'border-destructive' : ''}
            />
            {errors.failure && <p className="text-sm text-destructive">{errors.failure}</p>}
          </div>

          {/* Optional fields */}
          <div className="space-y-3 pt-2 border-t">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Optional</p>

            {/* Machine */}
            <div className="space-y-2">
              <Label>Machine</Label>
              <Select value={machineId || '__none__'} onValueChange={(v) => setMachineId(v === '__none__' ? undefined : v)}>
                <SelectTrigger><SelectValue placeholder="Select a machine" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {machines.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.machine_id} — {m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assign To */}
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={assignedTo || '__unassigned__'} onValueChange={(v) => setAssignedTo(v === '__unassigned__' ? undefined : v)}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {technicians.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Additional details..." className="min-h-[60px]" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Creating...' : 'Create Work Order'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
